// Karta "Komentarze i załączniki" - pobiera komentarze pozycji oferty z API
// i obsługuje akcje edycji / usunięcia. Wycięty jako osobny komponent,
// żeby hook useGetSalesCrmOfferPositionComments był wywoływany per rozwinięta pozycja.

import React, { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import { useQueryClient } from 'react-query';
import { Eye, EyeSlash } from 'react-bootstrap-icons';

import {
  AttachmentIcon,
  EditPencilIcon,
  PlusCircleIcon,
  RemoveIcon
} from 'assets/icons';

import {
  ISalesCrmOfferPositionComment,
  useDeleteSalesCrmOfferPositionComment,
  useGetSalesCrmOfferPositionComments
} from 'api';

import EditPositionCommentModal from '../EditPositionCommentModal';

import styles from 'theme/components/containers/TraderClientOfferProductsContainer/TraderClientOfferProductsContainer.module.scss';

interface IPositionCommentsCardProps {
  clientId: number;
  offerId: string;
  positionId: number;
  versionId?: number | null;
  /** Rozciąga kartę na dwie kolumny gridu (gdy Upsale jest tymczasowo wyłączone). */
  wide?: boolean;
  onAddComment: () => void;
}

const formatDate = (raw: string | null): string => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} | ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const PositionCommentsCard: FC<IPositionCommentsCardProps> = ({
  clientId,
  offerId,
  positionId,
  versionId = null,
  wide = false,
  onAddComment
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editingComment, setEditingComment] = useState<ISalesCrmOfferPositionComment | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const queryKey = [
    'sales-crm',
    'client',
    clientId,
    'offer',
    offerId,
    'position',
    positionId,
    'comments',
    versionId ?? null
  ];

  const { data, isLoading, isError } = useGetSalesCrmOfferPositionComments(
    clientId,
    offerId,
    positionId,
    versionId
  );

  const { mutate: deleteComment, isLoading: isDeleting } = useDeleteSalesCrmOfferPositionComment(
    clientId,
    offerId,
    positionId,
    {
      onSuccess: () => {
        setActionError(null);
        queryClient.invalidateQueries(queryKey);
      },
      onError: (error) => {
        setActionError(error?.message || t('Nie udało się usunąć komentarza.'));
      }
    }
  );

  const items = data?.items ?? [];

  const handleDelete = (commentId: number) => {
    if (isDeleting) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('Czy na pewno usunąć komentarz?'))) return;
    deleteComment(commentId);
  };

  return (
    <div className={`${styles.expandedCard}${wide ? ` ${styles.expandedCardWide}` : ''}`}>
      <div className={styles.expandedCardTitle}>{t('Komentarze i załączniki')}</div>

      {isLoading && (
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          <CircularProgress size={20} />
        </div>
      )}

      {!isLoading && isError && (
        <div style={{ color: '#e02e4e', fontSize: 12, marginBottom: 8 }}>
          {t('Nie udało się pobrać komentarzy.')}
        </div>
      )}

      {actionError && (
        <div style={{ color: '#e02e4e', fontSize: 12, marginBottom: 8 }}>{actionError}</div>
      )}

      {!isLoading &&
        !isError &&
        items.map((c, idx) => (
          <React.Fragment key={c.id}>
            <div className={styles.commentItem}>
              <div className={styles.commentHeader}>
                <div className={styles.commentAvatar}>
                  {c.authorType === 'CLIENT' ? 'K' : c.authorType === 'TRADER' ? 'H' : '?'}
                </div>
                <div className={styles.commentAuthor}>
                  {c.authorType === 'CLIENT'
                    ? t('Klient')
                    : c.authorType === 'TRADER'
                    ? t('Handlowiec')
                    : t('Komentarz')}
                </div>
                <div className={styles.commentDate}>{formatDate(c.createdAt)}</div>
                <div
                  className={styles.commentVisibleCheck}
                  title={c.isVisibleForClient ? t('Widoczny dla klienta') : t('Ukryty przed klientem')}>
                  {c.isVisibleForClient ? <Eye size={14} /> : <EyeSlash size={14} />}
                  <span>
                    {c.isVisibleForClient ? t('Widoczny dla klienta') : t('Ukryty przed klientem')}
                  </span>
                </div>
                {c.authorType !== 'CLIENT' && (
                  <div className={styles.commentActions}>
                    <button
                      type="button"
                      className={styles.commentActionBtn}
                      onClick={() => setEditingComment(c)}
                      aria-label={t('Edytuj komentarz')}>
                      <EditPencilIcon className={styles.commentActionIcon} />
                    </button>
                    <button
                      type="button"
                      className={styles.commentActionBtn}
                      onClick={() => handleDelete(c.id)}
                      aria-label={t('Usuń komentarz')}>
                      <RemoveIcon className={styles.commentActionIcon} />
                    </button>
                  </div>
                )}
              </div>

              {c.comment && <div className={styles.commentText}>{c.comment}</div>}

              {c.fileUrl && c.fileName && (
                <a
                  className={styles.commentAttachment}
                  href={c.fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  download={c.fileName}>
                  <span>{c.fileName}</span>
                  <AttachmentIcon className={styles.commentAttachmentIcon} />
                </a>
              )}
            </div>

            {idx < items.length - 1 && <hr className={styles.commentSeparator} />}
          </React.Fragment>
        ))}

      {!isLoading && !isError && items.length > 0 && <hr className={styles.commentSeparator} />}

      <button type="button" className={styles.addCommentLink} onClick={onAddComment}>
        {t('DODAJ KOMENTARZ / ZAŁĄCZNIK')} <PlusCircleIcon className={styles.plusCircleSm} />
      </button>

      {editingComment && (
        <EditPositionCommentModal
          open={editingComment !== null}
          clientId={clientId}
          offerId={offerId}
          positionId={positionId}
          commentId={editingComment.id}
          initialComment={editingComment.comment ?? ''}
          initialIsVisibleForClient={editingComment.isVisibleForClient}
          authorType={editingComment.authorType}
          onClose={() => setEditingComment(null)}
        />
      )}
    </div>
  );
};

export default PositionCommentsCard;
